import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import Select from 'react-select';
import { useFlags } from 'launchdarkly-react-client-sdk';

//Components
import { WpContainer, WpContainerHeader, WpContainerBody, WpContainerFooter } from 'Wpds/components/container';
import { WpButton } from 'Wpds/components/button';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import { updateProfile } from 'Shared/actions/user';
import { createAccessToken, getAccessToken } from 'Shared/actions/user';
import AvatarChangePic from 'Shared/components/AvatarChangePic/AvatarChangePic';
import DeleteAccountButton from './DeleteAccountButton/DeleteAccountButton';

//Hooks
import { useSmac } from 'Shared/utils/hooks/use-smac';
import { CurrentUser } from 'Shared/types/CurrentUser.type';

type Props = {
  submitButtonText?: string;
  user: CurrentUser;
};

function ProfileForm({ submitButtonText = 'Update', user }: Props) {
  const { zapierIntegration, enablePerformOrgManagement } = useFlags();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const timeZoneOptions = useSelector(state => state.default_options.time_zone_options);

  const selectedTimeZoneOption =
    user.person && user.person.timezone
      ? timeZoneOptions.find(({ value }) => value === user.person.timezone)
      : timeZoneOptions.find(({ value }) => value === moment.tz.guess(true));

  const [formValues, setFormValues] = useState({
    avatar: user.avatar,
    firstName: user.first_name,
    lastName: user.last_name,
    timeZone: selectedTimeZoneOption,
    email: user.email,
    access_token: null,
  });

  const [showAccessToken, setShowAccessToken] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const { isStandAlone, hasPi2Access } = useSmac();

  const disableFormField = enablePerformOrgManagement && (isStandAlone || hasPi2Access);

  useEffect(() => {
    dispatch(getAccessToken()).then(response => {
      if (response.data.token) {
        setFormValues({ ...formValues, access_token: response.data.token.token });
        setShowAccessToken(true);
      }
    });
  }, []);

  const setAvatarPic = useCallback(file => setFormValues({ ...formValues, avatar: file }), [formValues]);

  const handleTextFieldChange = useCallback(
    event => setFormValues({ ...formValues, [event.target.name]: event.target.value }),
    [formValues]
  );

  const handleTimeZoneChange = useCallback(value => setFormValues({ ...formValues, timeZone: value }), [formValues]);

  const handleSubmitForm = async event => {
    event.preventDefault();
    const submitValues = {
      id: user.id,
      selected_time_zone_option: formValues.timeZone,
      first_name: formValues.firstName,
      last_name: formValues.lastName,
      profile_picture: formValues.avatar,
    };
    setIsSubmitting(true);
    await dispatch(updateProfile(submitValues));
    setIsSubmitting(false);
  };

  const generateAcessToken = () => {
    setIsGenerating(true);
    dispatch(createAccessToken()).then(response => {
      setIsGenerating(false);
      setFormValues({ ...formValues, access_token: response.data.token.token });
      setShowAccessToken(true);
    });
  };

  return (
    <form onSubmit={handleSubmitForm}>
      <WpContainer>
        <WpContainerHeader>
          <h5>Account</h5>
        </WpContainerHeader>
        <WpContainerBody>
          <div className="form-group row">
            <label className="col-sm-4 col-form-label">Profile Pic</label>
            <div className="col-sm-8">
              <AvatarChangePic avatar={formValues.avatar} setAvatarPic={setAvatarPic} />
            </div>
          </div>
          <div className="form-group row">
            <label className="col-sm-4 col-form-label">First Name</label>
            <div className="col-sm-8">
              <input
                type="text"
                name="firstName"
                className="form-control"
                onChange={handleTextFieldChange}
                value={formValues.firstName}
                disabled={disableFormField}
              />
            </div>
          </div>
          <div className="form-group row">
            <label className="col-sm-4 col-form-label">Last Name</label>
            <div className="col-sm-8">
              <input
                type="text"
                name="lastName"
                className="form-control"
                onChange={handleTextFieldChange}
                value={formValues.lastName}
                disabled={disableFormField}
              />
            </div>
          </div>
          <div className="form-group row">
            <label className="col-sm-4 col-form-label">Time Zone</label>
            <div className="col-sm-8">
              <Select value={formValues.timeZone} options={timeZoneOptions} onChange={handleTimeZoneChange} />
            </div>
          </div>
          {zapierIntegration && (
            <div className="form-group row">
              <label className="col-sm-4 col-form-label">Access Token</label>
              <div className="col-sm-8">
                {showAccessToken && (
                  <WpTextField
                    id="copy-paste"
                    type="password"
                    label=""
                    value={formValues.access_token}
                    disabled
                    enableCopy
                  />
                )}
                {!showAccessToken && (
                  <WpButton onClick={generateAcessToken} loading={isGenerating}>
                    {' '}
                    Generate Access Token
                  </WpButton>
                )}
              </div>
            </div>
          )}
          <div className="form-group row">
            <label className="col-sm-4 col-form-label">Email</label>
            <div className="col-sm-8">
              <input type="email" className="form-control-plaintext" readOnly value={formValues.email} />

              <small className="form-text text-muted">
                Want to use another email? <a href="/user/logout">Sign out</a> and authenticate with another email.
              </small>
              <DeleteAccountButton />
            </div>
          </div>
        </WpContainerBody>
        <WpContainerFooter>
          <WpButton loading={isSubmitting} btnType="submit">
            {submitButtonText}
          </WpButton>
        </WpContainerFooter>
      </WpContainer>
    </form>
  );
}

export default ProfileForm;
